import { useCallback, useEffect, useState } from "react";
import { ShieldCheck, Check, Download, Send } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fmtDate } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  pending: "bg-rose-50 text-rose-700",
  registered: "bg-amber-100 text-amber-900",
  approved: "bg-emerald-50 text-emerald-700",
};
const STATUS_LABEL = { pending: "Pending", registered: "Registered", approved: "Approved" };

const Field = ({ label, value, testid }) => (
  <div data-testid={testid}>
    <p className="text-xs font-medium text-slate-500">{label}</p>
    <p className="mt-0.5 text-sm font-medium text-brand-ink">{value || "—"}</p>
  </div>
);

export const WarrantyDetails = ({ jobId, productType, label, status, certificateNo, warrantyId, canSend, onChanged }) => {
  const [d, setD] = useState(null);
  const [form, setForm] = useState({ customer_email: "", chassis_vin: "", warranty_notes: "", installation_date: "", invoice_date: "" });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    api
      .get(`/jobs/${jobId}/warranty-details`, {
        params: productType ? { product_type: productType } : {},
      })
      .then(({ data }) => {
        setD(data);
        setForm({
          customer_email: data.customer_email || "",
          chassis_vin: data.chassis_vin || "",
          warranty_notes: data.warranty_notes || "",
          installation_date: (data.installation_date || "").slice(0, 10),
          invoice_date: (data.invoice_date || "").slice(0, 10),
        });
        setDirty(false);
      })
      .catch(() => {});
  }, [jobId, productType]);

  useEffect(() => { load(); }, [load]);

  const change = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    setDirty(true);
    setSavedAt(false);
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      await api.patch(`/jobs/${jobId}/warranty-details`, {
        ...form,
        ...(productType ? { product_type: productType } : {}),
      });
      setDirty(false);
      setSavedAt(true);
      toast.success("Warranty details saved");
      onChanged && onChanged();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not save warranty details");
    } finally {
      setSaving(false);
    }
  };

  if (!d) return null;
  const vehicle = [d.vehicle_make, d.vehicle_model, d.vehicle_year].filter(Boolean).join(" ");
  const testSuffix = productType ? `-${productType.replace(/\s+/g, "-").toLowerCase()}` : "";

  const sendToCustomer = async () => {
    if (!warrantyId) return;
    setSending(true);
    try {
      const { data } = await api.post(`/warranties/${warrantyId}/send`);
      if (data.email?.sent)
        toast.success(`Sent to ${Array.isArray(data.email.to) ? data.email.to.join(", ") : data.email.to}`);
      else toast.warning(`Marked sent — email not delivered (${data.email?.reason || "no email on file"})`);
      onChanged && onChanged();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="border-brand-line shadow-none" data-testid={`warranty-details-section${testSuffix}`}>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2 text-lg">
            <ShieldCheck className="h-5 w-5 text-brand" /> Warranty Details
            {label && (
              <Badge className="border-0 bg-brand-tint text-brand" data-testid={`wd-product-label${testSuffix}`}>
                {label}
              </Badge>
            )}
            {status && (
              <Badge
                data-testid={`wd-status${testSuffix}`}
                className={`border-0 ${STATUS_STYLE[status] || STATUS_STYLE.pending}`}
              >
                {STATUS_LABEL[status] || "Pending"}
              </Badge>
            )}
          </CardTitle>
          <p className="mt-1 text-xs text-slate-500">
            {label ? `${label} warranty — reference & data-entry panel. ` : "Reference & data-entry panel for warranty registration. "}
            Enter the editable fields below and tap <span className="font-semibold text-brand-ink">Save details</span> to store them.
            {certificateNo && (
              <span className="font-medium text-emerald-700" data-testid={`wd-cert${testSuffix}`}>
                {" "}Certificate {certificateNo}
              </span>
            )}
          </p>
        </div>
        {warrantyId && (
          <div className="flex flex-wrap gap-2" data-testid={`wd-actions${testSuffix}`}>
            <Button
              size="sm"
              variant="outline"
              className="min-h-[40px]"
              data-testid={`wd-view-pdf${testSuffix}`}
              onClick={() => window.open(`${API}/warranties/${warrantyId}/certificate`, "_blank")}
            >
              <Download className="mr-1.5 h-4 w-4" /> View PDF
            </Button>
            {canSend && status === "registered" && (
              <Button
                size="sm"
                className="min-h-[40px]"
                disabled={sending}
                data-testid={`wd-send${testSuffix}`}
                onClick={sendToCustomer}
              >
                <Send className="mr-1.5 h-4 w-4" /> {sending ? "Sending…" : "Send to Customer"}
              </Button>
            )}
            {status === "approved" && (
              <span className="flex items-center gap-1 text-xs font-medium text-emerald-700" data-testid={`wd-sent${testSuffix}`}>
                <Check className="h-3.5 w-3.5" /> Sent to customer
              </span>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Customer name" value={d.customer_name} testid="wd-customer-name" />
          <Field label="Customer phone" value={d.customer_phone} testid="wd-customer-phone" />
          <Field label="Customer WhatsApp" value={d.customer_whatsapp} testid="wd-customer-whatsapp" />
          <Field label="Vehicle" value={vehicle} testid="wd-vehicle" />
          <Field label="Registration number" value={d.registration_no} testid="wd-registration" />
          <Field label="Job reference" value={d.job_ref} testid="wd-job-ref" />
          <Field label="Studio" value={d.studio_name} testid="wd-studio-name" />
          <Field label="Studio email" value={d.studio_email} testid="wd-studio-email" />
        </div>

        <div>
          <p className="text-xs font-medium text-slate-500">Service(s) applied</p>
          <div className="mt-1 flex flex-wrap gap-1.5" data-testid="wd-services">
            {(d.services || []).length
              ? d.services.map((s) => <Badge key={s} className="border-0 bg-brand-tint text-brand">{s}</Badge>)
              : <span className="text-sm text-slate-400">—</span>}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <p className="text-xs font-medium text-slate-500">Material used</p>
            <div className="mt-1 space-y-1" data-testid="wd-materials">
              {(d.materials || []).length
                ? d.materials.map((m, i) => (
                    <p key={i} className="text-sm text-brand-ink">
                      {m.name} <span className="text-slate-400">· {m.quantity} {m.unit}</span>
                    </p>
                  ))
                : <span className="text-sm text-slate-400">No material logged yet</span>}
            </div>
          </div>
          <div>
            <p className="text-xs font-medium text-slate-500">Roll serial number(s)</p>
            <div className="mt-1 flex flex-wrap gap-1.5" data-testid="wd-serials">
              {(d.serials || []).length
                ? d.serials.map((s) => <Badge key={s} className="border-0 bg-slate-100 font-mono text-slate-700">{s}</Badge>)
                : <span className="text-sm text-slate-400">No serial recorded yet</span>}
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-brand-line bg-slate-50/60 p-4">
          <div className="mb-3 flex items-center justify-between gap-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Editable — saved against this job
            </p>
            {dirty && (
              <span className="text-xs font-medium text-amber-600" data-testid={`wd-unsaved${testSuffix}`}>
                Unsaved changes
              </span>
            )}
            {savedAt && !dirty && (
              <span className="flex items-center gap-1 text-xs font-medium text-emerald-600" data-testid={`wd-saved${testSuffix}`}>
                <Check className="h-3.5 w-3.5" /> Saved
              </span>
            )}
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="text-xs">Customer email</Label>
              <Input
                className="mt-1"
                type="email"
                data-testid="wd-input-email"
                value={form.customer_email}
                onChange={(e) => change("customer_email", e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs">Chassis number / VIN</Label>
              <Input
                className="mt-1"
                data-testid="wd-input-vin"
                value={form.chassis_vin}
                onChange={(e) => change("chassis_vin", e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs">Invoice date</Label>
              <Input
                className="mt-1"
                type="date"
                data-testid="wd-input-invoice-date"
                value={form.invoice_date}
                onChange={(e) => change("invoice_date", e.target.value)}
              />
              <p className="mt-1 text-[11px] text-slate-400">Warranty starts from this date, not the registration date.</p>
            </div>
            <div>
              <Label className="text-xs">Installation date</Label>
              <Input
                className="mt-1"
                type="date"
                data-testid="wd-input-install-date"
                value={form.installation_date}
                onChange={(e) => change("installation_date", e.target.value)}
              />
              <p className="mt-1 text-[11px] text-slate-400">Leave blank to use the job's delivery date.</p>
            </div>
          </div>
          <div className="mt-4">
            <Label className="text-xs">Additional warranty notes</Label>
            <Textarea
              className="mt-1"
              rows={3}
              data-testid="wd-input-notes"
              value={form.warranty_notes}
              onChange={(e) => change("warranty_notes", e.target.value)}
            />
          </div>
          <div className="mt-4 flex justify-end">
            <Button
              size="sm"
              className="min-h-[40px]"
              disabled={saving || !dirty}
              data-testid={`wd-save${testSuffix}`}
              onClick={saveAll}
            >
              <Check className="mr-1.5 h-4 w-4" />
              {saving ? "Saving…" : dirty ? "Save details" : "Saved"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default WarrantyDetails;
